// app/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { handleExtendActiveSession, useUserStore } from '@/entities/user';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { useConstructor, useEmitter, useSetState } from '@/shared/lib/customHooks';
import type { ExtendActiveSessionSocketMessage } from '@/shared/lib';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import type { DeactiveTimeTrackingProps } from './deactiveTimeTracking';

interface DeactiveTimeTrackingState {
  openConfirmYourActivityModal: boolean;
  openSessionTimedOutModal: boolean;
  isConfirmingYourActivity: boolean;
  isGottingIt: boolean;
}

const DEFAULT_INACTIVE_TIMEOUT_DURATION = 25 * 60 * 1000; // 25 minutes
const DEFAULT_EXTEND_ACTIVE_SESSION_TIMEOUT_DURATION = 2 * 60 * 1000; // 2 minutes

/**
 * Tracks user inactivity for Biocare portal sessions, prompting to confirm activity and timing out.
 * When signed in through the Biocare portal it extends the session on mount, listens to mouse
 * movement and tab visibility to run the idle and extend-session timers, and restarts them on
 * unexpired extend-session socket events for the current session.
 *
 * @param props - The Biocare login flag and the Biocare sign-out and redirect callbacks.
 * @returns The modal state, the confirm modal ref and the confirm, timed-out and got-it handlers.
 */
export const useDeactiveTimeTracking = ({
  isLoginWithBiocarePortal,
  handleOnlySignOutBiocarePortal,
  handleRedirectToBiocarePortal,
}: DeactiveTimeTrackingProps) => {
  /**
   * Holds the signed-in user record taken from the user store. Subscribes to the store's me slice so the hook re-runs whenever the current user changes.
   *
   * @returns The current user record from the user store.
   */
  const meRedux = useUserStore((s) => s.me);

  const [state, setState] = useSetState<DeactiveTimeTrackingState>({
    openConfirmYourActivityModal: false,
    openSessionTimedOutModal: false,
    isConfirmingYourActivity: false,
    isGottingIt: false,
  });

  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
  const shouldExtendActiveSessionTimer = useRef<NodeJS.Timeout | null>(null);
  const shouldExtendActiveSession = useRef(false);
  const isExtendingActiveSession = useRef(false);
  const confirmYourActivityModalRef = useRef<{ clearTimer: () => void } | null>(null);

  /**
   * Stops the countdown that would open the inactivity confirmation prompt. Cancels the inactivity timeout, nulls its handle and tells the confirm-your-activity modal to clear its own countdown.
   *
   * @returns Nothing.
   */
  const clearInactivityTimer = () => {
    clearTimeout(inactivityTimer.current ?? undefined);
    inactivityTimer.current = null;

    confirmYourActivityModalRef.current?.clearTimer();
  };

  /**
   * Cancels the pending session-extension window so further mouse activity no longer refreshes the session. Clears the should-extend flag, cancels the scheduled timeout and drops the stored timer handle.
   *
   * @returns Nothing.
   */
  const clearExtendActiveSessionTimer = () => {
    shouldExtendActiveSession.current = false;
    clearTimeout(shouldExtendActiveSessionTimer.current ?? undefined);
    shouldExtendActiveSessionTimer.current = null;
  };

  /**
   * Starts the idle countdown that asks the user to confirm they are still active. Schedules a timeout for the configured inactive duration which opens the confirm-your-activity modal.
   *
   * @returns Nothing.
   */
  const handleInactiveTimeout = () => {
    inactivityTimer.current = setTimeout(() => {
      setState({ openConfirmYourActivityModal: true });
    }, DEFAULT_INACTIVE_TIMEOUT_DURATION);
  };

  /**
   * Resets the idle tracking cycle after activity has been observed. Clears the inactivity and extend-session timers, then schedules a fresh inactivity countdown.
   *
   * @returns Nothing.
   */
  const restartTimers = () => {
    clearInactivityTimer();
    clearExtendActiveSessionTimer();
    handleInactiveTimeout();
  };

  /**
   * Arms the delay after which the next user movement should extend the active session. Schedules a timeout that flips the should-extend flag on once the configured extend-session duration has passed.
   *
   * @returns Nothing.
   */
  const handleExtendActiveSessionTimeout = () => {
    shouldExtendActiveSessionTimer.current = setTimeout(() => {
      shouldExtendActiveSession.current = true;
    }, DEFAULT_EXTEND_ACTIVE_SESSION_TIMEOUT_DURATION);
  };

  /**
   * Treats pointer movement as activity and renews the session once the extend window has elapsed. Ignores movement while an extension is in flight; when the extend flag is set it clears the extend timer, awaits the extension and re-arms it; otherwise it arms the extend timer if missing and restarts the idle timers while the tab is visible.
   *
   * @returns A promise resolving once the session extension and the timers have been settled.
   */
  const handleMouseMove = async () => {
    if (isExtendingActiveSession.current) {
      return;
    }

    if (shouldExtendActiveSession.current) {
      isExtendingActiveSession.current = true;
      clearExtendActiveSessionTimer();
      await handleExtendActiveSession();
      handleExtendActiveSessionTimeout();
      isExtendingActiveSession.current = false;
      return;
    }

    if (!shouldExtendActiveSessionTimer.current) {
      handleExtendActiveSessionTimeout();
    }

    if (document.visibilityState === 'visible') {
      if (!inactivityTimer.current) {
        restartTimers();
      }
    }
  };

  /**
   * Reacts to the tab being hidden or shown so idle tracking matches real presence. On becoming visible it logs the return, restarts the idle timers and awaits a session extension, logging failures; when hidden it logs the inactivity and clears both the inactivity and extend-session timers.
   *
   * @returns A promise resolving once the timers have been restarted or cleared for the new visibility.
   */
  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible') {
      console.log('User has returned to the page.');
      try {
        restartTimers();
        await handleExtendActiveSession();
      } catch (error) {
        console.error('Failed to extend active session:', error);
      }
    } else {
      console.log('User has gone inactive.');
      clearInactivityTimer();
      clearExtendActiveSessionTimer();
    }
  };

  /**
   * Confirms the user is still present and renews the active session. Flags the confirmation as in progress, awaits the session extension and logs any failure, then in all cases closes the modal, clears the flags and restarts the idle timers.
   *
   * @returns A promise resolving once the session has been extended and the modal closed.
   */
  const handleConfirmYourActivityModal = async () => {
    try {
      setState({ isConfirmingYourActivity: true });
      await handleExtendActiveSession();
    } catch (error) {
      console.error('Failed to extend active session:', error);
    } finally {
      setState({
        openConfirmYourActivityModal: false,
        isConfirmingYourActivity: false,
      });
      restartTimers();
    }
  };

  /**
   * Ends an idle session and shows the timed-out notice. Clears both the inactivity and extend-session timers, signs the user out of the Biocare portal only, then closes the confirm-your-activity modal and opens the session-timed-out modal.
   *
   * @returns A promise resolving once the sign-out has run and the timed-out modal opened.
   */
  const handleSessionTimedOutModalDisplay = async () => {
    clearInactivityTimer();
    clearExtendActiveSessionTimer();
    handleOnlySignOutBiocarePortal();
    setState({
      openConfirmYourActivityModal: false,
      openSessionTimedOutModal: true,
    });
  };

  /**
   * Acknowledges the session-timed-out notice and sends the user back to the Biocare portal. Marks the got-it action as in progress in state and triggers the Biocare portal redirect.
   *
   * @returns Nothing.
   */
  const handleGotItClick = () => {
    setState({ isGottingIt: true });
    handleRedirectToBiocarePortal();
  };

  useConstructor(() => {
    if (isLoginWithBiocarePortal) {
      if (!state.openConfirmYourActivityModal && !state.openSessionTimedOutModal) {
        (async () => {
          await handleExtendActiveSession();
        })();
      }
    }
  });

  useEffect(() => {
    if (isLoginWithBiocarePortal) {
      if (!state.openConfirmYourActivityModal && !state.openSessionTimedOutModal) {
        document.addEventListener('mousemove', handleMouseMove);
      }
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [
    isLoginWithBiocarePortal,
    state.openConfirmYourActivityModal,
    state.openSessionTimedOutModal,
  ]);

  useEffect(() => {
    if (isLoginWithBiocarePortal) {
      if (!state.openConfirmYourActivityModal && !state.openSessionTimedOutModal) {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    isLoginWithBiocarePortal,
    state.openConfirmYourActivityModal,
    state.openSessionTimedOutModal,
  ]);

  useEmitter(
    EMITTER_CONSTANTS.EXTEND_ACTIVE_SESSION,
    (msg: ExtendActiveSessionSocketMessage) => {
      if (document.visibilityState === 'visible') {
        if (meRedux?.sessionId === msg.sessionId) {
          if (msg.expiredAt) {
            if (isLoginWithBiocarePortal) {
              if (!state.openConfirmYourActivityModal && !state.openSessionTimedOutModal) {
                const isExpired = dayjs().isAfter(dayjs(msg.expiredAt));
                if (!isExpired) {
                  console.log('Restarting timers due to extend active session event');
                  restartTimers();
                }
              }
            }
          }
        }
      }
    },
    [
      isLoginWithBiocarePortal,
      meRedux?.sessionId,
      state.openConfirmYourActivityModal,
      state.openSessionTimedOutModal,
    ],
  );

  return {
    state,
    confirmYourActivityModalRef,
    handleConfirmYourActivityModal,
    handleSessionTimedOutModalDisplay,
    handleGotItClick,
  };
};
